<template>
  <ims-page :requireLogin="true">
    <!-- Page Header -->
    <template v-slot:header>
      <ims-toolbar v-if="league">
        <template v-slot:left>
          <ims-back-button />
        </template>
        <span class="title md mx-4">{{ league.leagueName }}</span>
        <template v-slot:right>
          <ims-button
            v-if="league.isAdmin"
            color="blue"
            type="clear"
            size="sm"
            class="btn"
            @click="viewAdmin(league.leagueId)"
          >Admin</ims-button>
        </template>
      </ims-toolbar>
      <!-- Tab Bar -->
    </template>
    <main class="league-details container p-0">
      <div class="desktop-bumper" />

      <section class="league-standings">
        <ims-item class="divider">
          <!-- Divider standings overall rank -->
          <div class="ims-row">
            <span class="text-sm">Standings</span>
            <div class="filler mx-2 text-center">
              <ims-spinner :size="16" v-if="loading" />
              <h3 v-if="overall" class="text-md text-dark">Overall {{ overall.rank | ordinal }}</h3>
            </div>
            
            <!-- If race is live, and if we're refreshing -->
            <span
              class="text-sm"
              v-if="league && hasLive && $AppConfig.refreshLeagueDetails"
            >refresh on</span>
            <span class="text-sm mr-4 pr-5" v-else></span>
          </div>
          <div
              class="text-sm all-player-details"
              v-if="league && hasLive && league.isPermanent"
            >Details for each race and overall are not available while race is in progress</div>
        </ims-item>
        <!-- No Standings  -->
        <ims-item v-if="standings.length == 0" class="pad pt-2">
          <span v-if="loading">Standings Loading...</span>
          <span v-else>Standings Unavailable</span>
        </ims-item>

        <!-- Loop over standings -->
        <ims-item
          v-for="(standing, index) in standings"
          :key="index"
          :to="(league.isPermanent && hasLive) ? `` : `/leagues/${standing.leagueId}/races/${standing.raceId}`"
          :class="index == races.length - 1 ? 'ims-item-last' : ''"
        >
          <template v-slot:left>
            <div
              class="race-logo"
              v-if="standing.race"
              :style="`background-image:url(${standing.race.logoUrl});`"
            ></div>
          </template>

          <h1 class="text-sm">{{ standing.race.name }}</h1>

          <template v-slot:right>
            <ims-badge size="sm" v-if="standing.rank" color="gray">{{ standing.rank | ordinal }}</ims-badge>
            <ims-badge
              class="pl-2 ml-2"
              size="sm"
              v-if="standing.race.status === $AppConfig.status.IN_PROGRESS"
              color="red"
            >
              LIVE
              <ims-icon icon="keyboard_arrow_right" size="sm" color="white" />
            </ims-badge>
            <ims-icon
              icon="keyboard_arrow_right"
              v-if="standing.race.status !== $AppConfig.status.IN_PROGRESS"
            />
          </template>
        </ims-item>

        <!-- Overall Standings -->
        <ims-item v-if="league" class="mt-3" :to="(league.isPermanent && hasLive) ? `` : `/leagues/${league.leagueId}/overall`" >
          <template v-slot:left>
            <h1 class="text-lg">All Races</h1>
          </template>
          <template v-slot:right>
            <ims-icon icon="keyboard_arrow_right" class="ml-5" />
          </template>
        </ims-item>
      </section>
      <section class="league-options mt-5">
        <ims-item class="border-top p-1" v-if="league && !league.isAdmin && !league.isPermanent">
          <ims-button type="clear" color="grey" @click="leaveLeague">Leave League</ims-button>
        </ims-item>
      </section>
    </main>
  </ims-page>
</template>

<script>
import AppConfig from '@/AppConfig';

export default {
  data() {
    return {
      detail: null,
      loading: true,
      league: null,
      standings: [],
      hasLive: false,
      interval: null,
      overall: null
    };
  },
  created() {},
  mounted() {
    this.init();
  },
  computed: {
    races() {
      return this.$store.state.races.races;
    }
  },
  watch: {
    /**
     * Watch for Changes in the Races store
     * This watcher has an immediate:true
     * meaning it will fire right when the component
     * mounts.
     */
    '$store.state.races.races': {
      handler(races) {
        // Find if we have live races
        let live = this.races.find(
          // TODO CHANGE TO IN_PROGRESS
          race => race.status == AppConfig.status.IN_PROGRESS
        );
        // Set has live  if we have a live race
        this.hasLive = live ? true : false;
        if (this.hasLive) {
          this.startAutoReload();
        }
      },
      immediate: true
    }
    /**
     * Watch for User Login Change
     */
  },
  destroyed() {
    this.stopAutoReload();
  },
  methods: {
    /**
     * Intitalize the Data for
     * League Detail
     */
    async init() {
      let closeLoader = await this.$store.dispatch(
        'loader/start',
        'Getting League Details...'
      );
      // If store is empty - go load it
      if (this.$store.state.leagues.mine.length === 0) {
        await this.$store.dispatch('leagues/mine/load');
      }
      // If no races, load it.
      if (this.races.length === 0) {
        await this.$store.dispatch('races/load');
      }
      // Hide load - for thigns to update
      closeLoader();
      // Get league from store
      try {
        // Call Data Loader - will be used in auto reloading
        await this.loadData();
      } catch (e) {
        this.$store.dispatch('interact/error', e.message);
      }

      this.$forceUpdate();
    },
    async startAutoReload() {
      // Get time from config
      // Make sure it's more than 1000 otherwise it can
      // hit the API at an insane clip.
      let milliseconds = this.$AppConfig.refreshMS;
      milliseconds = milliseconds < 1000 ? 1000 : milliseconds;
      // If we have no interval running, and we're supposed to refresh the data
      if (!this.interval && AppConfig.refreshLeagueDetails) {
        console.log('IMS :: Starting Auto Reload');
        // Set the interval
        this.interval = setInterval(() => {
          this.loadData();
        }, milliseconds);
      }
    },
    async stopAutoReload() {
      clearInterval(this.interval);
    },
    async loadData() {
      this.loading = true;
      this.league = await this.$store.getters.getLeague(this.$route.params.id);
      // Get Leage Standings
      const standingPayload = await this.$User.service.getLeagueStandings(
        this.$route.params.id
      );
      // Standings for all races
      this.standings = standingPayload.standings;
      // Overall race - added March 5th 2020
      this.overall = standingPayload.overall;
      /**
       * Get Standings and Sorty
       * #6784 - sorting by race.greenFlagDateTime - newest first
       */
      this.standings = this.standings
        .map(standing => {
          // Associate Race with this standing
          let race = this.$store.getters['race/byId'](standing.raceId);
          // Add race to standing
          standing.race = race;
          // return standing
          return standing;
        })
        .sort((a, b) => {
          return a.race.greenFlagDateTime > b.race.greenFlagDateTime ? -1 : 1;
        });

      this.loading = false;
    },
    /**
     * Actually Leave the League
     */
    async _leaveLeague() {
      let stopLeaveLoader = await this.$store.dispatch(
        'loader/start',
        'Leaving league...'
      );
      try {
        // Call service
        let leave = await this.$User.service.leaveLeague(this.league.leagueId);
        // If delete is true
        if (leave) {
          // Fire toast
          this.$store.dispatch('interact/toast', {
            title: 'League Left'
          });
          // Go back
          this.$router.back(1);
        }
      } catch (e) {
        // Display error
        this.$store.dispatch('interact/toast', {
          title: `Error: ${e.message}`,
          timeout: 3000
        });
      }
      stopLeaveLoader();
    },
    viewAdmin(id) {
      this.$router.push({ name: 'league-admin', params: { id: id } });
    },
    leaveLeague() {
      const self = this;
      this.$store.dispatch('interact/popmenu', {
        title: 'Are you sure you want to leave this league?',
        buttons: [
          {
            title: 'Leave League',
            click: async () => {
              this._leaveLeague();
            }
          }
        ]
      });
    }
  }
};
</script>

<style lang="scss">
@import '../scss/utils/variables';
.league-details {
  .race-logo {
    width: 46px;
    height: 46px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
  .all-player-details{
    font-weight: normal;
    text-align: center;
    font-style: italic;
  }
}
</style>
